import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import type { ProductReference } from "../../types/types.PIM";
import { TablePlaceholder } from "../../util/util";
import { CardProductCompact } from "../CardProductCompact/CardProductCompact";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr));
  align-items: stretch;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  width: 100%;
`;

const GridCell = styled.div`
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;

  & > * {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
`;

const CardLink = styled(Link)`
  display: flex;
  flex: 1;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  min-width: 0;
  height: 100%;
`;

const NonClickableCard = styled(GridCell)`
  cursor: default;
  pointer-events: none;

  & > div {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
`;

export type ProductCardGridItem = {
  id: string;
  product: ProductReference;
  href?: string | null;
  dynamicPreviewBlocks?: { header: string; content: string }[];
  onProductClick?: (product: ProductReference) => void;
};

export type InaccessibleProductCardItem = {
  id: string;
  product: ProductReference;
  message: string;
};

export type ProductCardsGridProps = {
  items: ProductCardGridItem[];
  inaccessibleItems?: InaccessibleProductCardItem[];
  emptyMessage?: string;
};

export const ProductCardsGrid = ({
  items,
  inaccessibleItems = [],
  emptyMessage,
}: ProductCardsGridProps) => {
  const { t } = useTranslation();
  const placeholderMessage = emptyMessage ?? t("No items to show.");

  if (!items.length && !inaccessibleItems.length) {
    return <TablePlaceholder message={placeholderMessage} />;
  }

  return (
    <Grid>
      {items.map(
        ({ id, product, href, dynamicPreviewBlocks, onProductClick }) => {
          const card = (
            <CardProductCompact
              product={product}
              variant="tooltip"
              onProductClick={onProductClick}
              dynamicPreviewBlocks={
                dynamicPreviewBlocks && dynamicPreviewBlocks.length > 0
                  ? dynamicPreviewBlocks
                  : undefined
              }
            />
          );

          return (
            <GridCell key={id}>
              {href ? <CardLink to={href}>{card}</CardLink> : card}
            </GridCell>
          );
        }
      )}
      {inaccessibleItems.map(({ id, product, message }) => (
        <NonClickableCard key={id}>
          <CardProductCompact
            product={{
              ...product,
              brand_name: "",
              chemical_name: "",
              cas_number: "",
              product_description: "",
            }}
            variant="tooltip"
            dynamicPreviewBlocks={[{ header: "", content: message }]}
          />
        </NonClickableCard>
      ))}
    </Grid>
  );
};
